import React from 'react';

import type { AssetPrewarmingService } from './AssetPrewarmingService';
import type { ServiceContainer } from './ServiceContainer';

const ServiceContainerContext = React.createContext<ServiceContainer | null>(
  null,
);

/**
 * Provides a `ServiceContainer` to the React tree. Mount once at the app
 * root (or per-Storybook-story), holding the same container instance that
 * was injected into stores at bootstrap.
 */
export const ServiceContainerProvider: React.FC<{
  container: ServiceContainer;
  children: React.ReactNode;
}> = ({ container, children }) => (
  <ServiceContainerContext.Provider value={container}>
    {children}
  </ServiceContainerContext.Provider>
);

/**
 * Access the `ServiceContainer`. Throws if used outside a
 * `<ServiceContainerProvider>` — components that depend on services
 * MUST be mounted inside one.
 */
export function useServiceContainer(): ServiceContainer {
  const container = React.useContext(ServiceContainerContext);
  if (!container) {
    throw new Error(
      'useServiceContainer must be used inside <ServiceContainerProvider>',
    );
  }
  return container;
}

/** Convenience hook — returns the singleton `AssetPrewarmingService`. */
export function useAssetPrewarmingService(): AssetPrewarmingService {
  return useServiceContainer().assetPrewarming;
}
